// Pure logic for the training-calendar subscription on the account card.
//
// Lives outside the component so it can be tested without a DOM, matching
// accountForm.js. The component keeps rendering and state.

/**
 * What the athlete is told before they share the link.
 *
 * The URL is the credential — a calendar app cannot sign in — so anybody it is
 * forwarded to can read it. Saying exactly what that exposes, and what it does
 * not, is what lets somebody decide whether to paste it into a shared family
 * calendar.
 */
export const SUBSCRIPTION_NOTE =
  'Anyone with this link can see your session names and dates. ' +
  'It never shows loads, percentages or notes. ' +
  'If it reaches somebody it should not, rotate it: the old link stops working at once.';

/**
 * The absolute feed URL for a path the server returned.
 *
 * The API base may be relative ("/api") in a deployment that serves both from
 * one origin. A calendar app is handed this URL outside the page, so a relative
 * one would resolve against nothing.
 *
 * @param {string} path e.g. "/calendar/abc.ics", relative to the API base
 * @param {string} apiBase the configured API base URL
 * @param {string} origin the page's origin, for a relative base
 * @returns {string} '' when there is no path
 */
export function feedUrl(path, apiBase, origin) {
  if (!path) return '';
  try {
    return new URL(`${apiBase ?? ''}${path}`, origin).href;
  } catch {
    return '';
  }
}

/**
 * The same URL with the webcal scheme, which is what makes a click open the
 * calendar app's "subscribe" flow instead of downloading a one-off file that
 * never updates.
 */
export function webcalUrl(url) {
  if (!url) return '';
  return url.replace(/^https?:/i, 'webcal:');
}

/**
 * What the feed's state is, as far as this screen knows.
 *
 * UNKNOWN is its own state, not "off". The status request can fail, and a
 * screen that read that as "no link" would offer to create one — which
 * replaces the link the athlete already has and breaks every calendar
 * subscribed to it, while saying only "Link created".
 */
export const FEED_STATE = {
  LOADING: 'loading',
  OFF: 'off',
  ON: 'on',
  UNKNOWN: 'unknown',
};

/** The status endpoint's answer, as a FEED_STATE. */
export function feedStateFrom(data) {
  if (data?.active === true) return FEED_STATE.ON;
  if (data?.active === false) return FEED_STATE.OFF;
  return FEED_STATE.UNKNOWN;
}

/**
 * Whether issuing a link from this state may replace a live one, and so must
 * be confirmed. Only a feed known to be off is safe to create without asking.
 */
export function issuingReplaces(state) {
  return state !== FEED_STATE.OFF;
}

export const ROTATE_CONFIRM =
  'Replace your calendar link? The current link stops working at once, ' +
  'and every calendar subscribed to it stops updating.';

export const TURN_OFF_CONFIRM =
  'Turn off your calendar link? It stops working at once, ' +
  'and every calendar subscribed to it stops updating.';
